import React, { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import {
  Box,
  Checkbox,
  Button,
  Card,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Filter } from "../../app/config/controls";
import { Table, Pagination } from "../../app/config/tables";
import ConfirmService from "../../app/components/theme/Confirm/service";
import SnackbarService from "../../app/components/theme/Snackbar/service";
import api from "./api";

const DuplicateCatalogCuration = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [metadata, setMetadata] = useState<any>({ total: 0, pages: 1, pageNumber: 1 });
  const [page, setPage] = useState(0);
  const rowsPerPage = 100;
  const [textSearch, setTextSearch] = useState("");
  const [selectedGroup, setSelectedGroup] = useState<any | null>(null);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [groupDetail, setGroupDetail] = useState<any | null>(null);
  const [selectedOfficialId, setSelectedOfficialId] = useState<string | null>(null);

  const load = useCallback(
    _.debounce((currTextSearch: string, currPage: number) => {
      api.load({
        textSearch: currTextSearch || undefined,
        pageNumber: currPage + 1,
        pageSize: rowsPerPage,
      }).then((response: any) => {
        setRows(response.items || []);
        setMetadata(response.pagination || { total: 0, pages: 1, pageNumber: 1 });
      });
    }, 350),
    []
  );

  const refresh = () => load(textSearch, page);

  useEffect(() => {
    refresh();
  }, [textSearch, page]);

  const handleChangePage = (_: any, newPage: number) => setPage(newPage);

  const handleTextSearchChange = (event: any) => {
    setTextSearch(event.target.value);
    setPage(0);
  };

  const handleOpenGroup = (row: any) => {
    setSelectedGroup(row);
    setSelectedOfficialId(null);
    api.getGroup(row.groupId).then((response: any) => {
      setGroupDetail(response.group);
      setSelectedOfficialId(response.group?.officialDeviceModelId || null);
    });
  };

  const handleToggleGroup = (groupId: string) => {
    setSelectedGroupIds((current) => current.includes(groupId)
      ? current.filter((id) => id !== groupId)
      : [...current, groupId]);
  };

  const handleOpenSelectedGroups = () => {
    if (selectedGroupIds.length < 2) return;
    setSelectedOfficialId(null);
    Promise.all(selectedGroupIds.map((groupId) => api.getGroup(groupId))).then((responses) => {
      const groups = responses.map((response: any) => response.group).filter(Boolean);
      const variants = Array.from(new Map(
        groups.flatMap((group: any) => group.variants || []).map((variant: any) => [variant.deviceModelId, variant])
      ).values()).sort((a: any, b: any) => (b.orderCount || 0) - (a.orderCount || 0));
      setSelectedGroup({ groupIds: selectedGroupIds });
      setGroupDetail({
        ...groups[0], groupIds: selectedGroupIds,
        canonicalName: groups.map((group: any) => group.canonicalName).join(" + "),
        variantCount: variants.length,
        orderCount: groups.reduce((total: number, group: any) => total + (group.orderCount || 0), 0),
        variants,
      });
    });
  };

  const handleSetOfficial = (deviceModelId: string) => {
    setSelectedOfficialId(deviceModelId);
    SnackbarService.show("Modelo oficial seleccionado. Ahora puedes fusionar estos registros cuando quieras.", "success");
  };

  const handleMerge = () => {
    if (!selectedOfficialId) return;
    const callback = () =>
      (selectedGroup?.groupIds?.length > 1
        ? api.mergeMultiple(selectedGroup.groupIds, selectedOfficialId)
        : api.merge(selectedGroup?.groupId, selectedOfficialId)
      ).then(() => {
        refresh();
        setGroupDetail(null);
        setSelectedGroup(null);
        setSelectedGroupIds([]);
        setSelectedOfficialId(null);
      });
    ConfirmService.show(
      "Confirmar fusión",
      (
        <Box>
          <Typography sx={{ fontWeight: 800, mb: 1 }}>
            Esta acción redirige órdenes al oficial y archiva las variantes.
          </Typography>
          <Typography sx={{ color: "error.main" }}>
            No se borrarán registros ni se cambiarán IDs manualmente.
          </Typography>
        </Box>
      ),
      callback
    );
  };

  const stats = useMemo(
    () => ({
      groups: metadata?.total ?? 0,
      orders: rows.reduce((acc, row) => acc + (row.orderCount || 0), 0),
    }),
    [metadata, rows]
  );

  return (
    <Box sx={{ minHeight: "100%", p: { xs: 2, md: 3 }, borderRadius: 3, background: "linear-gradient(180deg, #F8FAFC 0%, #EEF2FF 100%)" }}>
      <Paper elevation={0} sx={{ mb: 3, p: { xs: 2, md: 3 }, borderRadius: 4, border: "1px solid rgba(139,92,246,0.12)", background: "linear-gradient(135deg, #FFFFFF 0%, #F5F3FF 100%)", boxShadow: "0 18px 45px rgba(15, 23, 42, 0.06)" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Stack spacing={1.25}>
              <Chip label="Modo limpieza" sx={{ width: "fit-content", fontWeight: 700, color: "#6D28D9", background: "linear-gradient(135deg, #EDE9FE, #DDD6FE)" }} />
              <Typography variant="h4" sx={{ fontWeight: 800, color: "#111827" }}>
                Modelos
              </Typography>
              <Typography sx={{ color: "#475569", maxWidth: 760 }}>
                Para fusionar dispositivos abre el detalle, selecciona una variante como oficial y confirma la fusión.
                El backend respeta la regla crítica: nada se borra y la fusión siempre requiere confirmación.
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Card sx={{ p: 2, borderRadius: 3, bgcolor: "rgba(255,255,255,0.72)" }}>
                  <Typography variant="caption" sx={{ color: "#64748B" }}>Grupos</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>{stats.groups}</Typography>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card sx={{ p: 2, borderRadius: 3, bgcolor: "rgba(255,255,255,0.72)" }}>
                  <Typography variant="caption" sx={{ color: "#64748B" }}>Órdenes</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>{stats.orders}</Typography>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <Card elevation={0} sx={{ borderRadius: 4, overflow: "hidden", border: "1px solid rgba(148,163,184,0.16)", boxShadow: "0 16px 40px rgba(15, 23, 42, 0.05)" }}>
        <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between", gap: 2, flexDirection: { xs: "column", md: "row" }, background: "linear-gradient(135deg, #111827 0%, #312E81 100%)", color: "white" }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Modelos duplicados</Typography>
            <Typography sx={{ opacity: 0.8 }}>Selecciona uno o varios grupos, elige una variante como oficial y confirma la fusión.</Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ alignSelf: { xs: "stretch", md: "flex-end" }, justifyContent: "flex-end" }}>
            <Button variant="contained" startIcon={<MergeTypeIcon />} disabled={selectedGroupIds.length < 2} onClick={handleOpenSelectedGroups} sx={{ bgcolor: "#8B5CF6", "&:hover": { bgcolor: "#7C3AED" } }}>
              Fusionar seleccionados ({selectedGroupIds.length})
            </Button>
            <Button variant="outlined" startIcon={<AutoFixHighIcon />} onClick={refresh} sx={{ borderColor: "rgba(255,255,255,0.3)", color: "white", "&:hover": { borderColor: "white" } }}>
              Recalcular
            </Button>
          </Stack>
        </Box>

        <Box sx={{ p: 2 }}>
          <Filter name="grupos de teléfono" value={textSearch} onChange={handleTextSearchChange} />
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell sx={{ fontWeight: 700 }}>Grupo</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Variantes</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Órdenes</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Riesgo</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.groupId} hover selected={selectedGroup?.groupId === row.groupId} sx={{ "&:hover": { backgroundColor: "rgba(139,92,246,0.04)" } }}>
                <TableCell padding="checkbox">
                  <Checkbox checked={selectedGroupIds.includes(row.groupId)} onChange={() => handleToggleGroup(row.groupId)} />
                </TableCell>
                <TableCell>
                  <Stack spacing={0.3}>
                    <Typography sx={{ fontWeight: 700 }}>{row.canonicalName || "Grupo sugerido"}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>{row.variantCount}</TableCell>
                <TableCell>{row.orderCount}</TableCell>
                <TableCell>
                  <Chip size="small" label={row.riskLevel || "Bajo"} sx={{ fontWeight: 700, bgcolor: row.riskLevel === "Alto" ? "rgba(239,68,68,0.14)" : row.riskLevel === "Medio" ? "rgba(245,158,11,0.14)" : "rgba(34,197,94,0.12)", color: row.riskLevel === "Alto" ? "#B91C1C" : row.riskLevel === "Medio" ? "#B45309" : "#15803D" }} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 260, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                    {row.reason || "Sugerido"}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                    <IconButton size="small" color="primary" onClick={() => handleOpenGroup(row)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Pagination rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} metadata={metadata} />

      </Card>

      <Drawer anchor="right" open={Boolean(groupDetail)} onClose={() => { setGroupDetail(null); setSelectedOfficialId(null); }}>
        <Box sx={{ width: { xs: "100vw", sm: 460 }, p: 3 }}>
          {groupDetail && (
            <Stack spacing={2.2}>
              <Box>
                <Typography variant="overline" sx={{ color: "#6D28D9", fontWeight: 800 }}>
                  Detalle del grupo
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {groupDetail.canonicalName}
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748B" }}>
                  {groupDetail.reason}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`${groupDetail.variantCount} variantes`} />
                <Chip label={`${groupDetail.orderCount} órdenes`} />
                <Chip label={groupDetail.requiresManualReview ? "Revisión manual" : "Auto-sugerido"} color={groupDetail.requiresManualReview ? "warning" : "success"} />
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button fullWidth variant="contained" startIcon={<MergeTypeIcon />} onClick={handleMerge} disabled={!selectedOfficialId} sx={{ bgcolor: "#8B5CF6", "&:hover": { bgcolor: "#7C3AED" } }}>
                  Fusionar
                </Button>
                <Button fullWidth variant="outlined" onClick={() => { setGroupDetail(null); setSelectedOfficialId(null); }}>
                  Cerrar
                </Button>
              </Stack>

              <Divider />

              <Stack spacing={1.2}>
                <Typography sx={{ fontWeight: 800 }}>Variantes</Typography>
                {groupDetail.variants?.map((variant: any) => {
                  const isSelected = selectedOfficialId === variant.deviceModelId;
                  return (
                    <Card key={variant.deviceModelId} sx={{ p: 1.5, borderRadius: 3, border: isSelected ? "1px solid rgba(34,197,94,0.35)" : "1px solid rgba(148,163,184,0.18)", bgcolor: isSelected ? "rgba(34,197,94,0.05)" : "white" }}>
                      <Stack spacing={1}>
                        <Box>
                        <Typography sx={{ fontWeight: 700 }}>{variant.model || "Modelo sin nombre"}</Typography>
                        <Typography variant="body2" sx={{ color: "#475569" }}>
                          Brand: {variant.brand || "Sin marca"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748B", display: "block" }}>
                          {variant.deviceModelId} | Órdenes: {variant.orderCount}
                        </Typography>
                      </Box>
                        <Stack direction="row" spacing={1}>
                          <Button size="small" variant={isSelected ? "contained" : "outlined"} startIcon={isSelected ? <CheckCircleIcon /> : undefined} onClick={() => handleSetOfficial(variant.deviceModelId)}>
                            {isSelected ? "Elegido como oficial" : "Elegir como oficial"}
                          </Button>
                        </Stack>
                      </Stack>
                    </Card>
                  );
                })}
              </Stack>
            </Stack>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default DuplicateCatalogCuration;
